import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  debounceTime,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  ADVANCED_FILTER_TYPE,
  AdvancedFiltersLookupScope,
  NO_PSIDS_FOUND_MESSAGE,
  SurveySummary,
} from '@ps/survey-utils';
import { PsidService, SurveyService } from '@ps/survey-data-access';

import { SurveyCreationActions } from '../audience/survey-creation-audience.actions';
import {
  AdvancedFilterActions,
  AdvancedFilterApiActions,
} from './survey-creation-insights.actions';

const SEARCH_DEBOUNCE_MS = 500;
const LOOKUP_DEBOUNCE_MS = 300;
const RESPONDENT_COUNT_DEBOUNCE_MS = 100;

/**
 * No pending/loading actions anywhere below: every request these effects make is
 * tagged `withLoading('drawer')`, so the drawer's spinner is driven off the HTTP
 * context. Debouncing matters for the same reason — each request that is
 * actually issued lights the spinner, so an undebounced burst reads as flicker.
 */
@Injectable()
export class SurveyCreationAdvancedFiltersEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);

  private readonly _surveyService = inject(SurveyService);
  private readonly _psidService = inject(PsidService);
  private readonly _toastService = inject(ToastService);

  /**
   * Searches for surveys based on the selected criteria and date range. In
   * Include mode, the raw lookup result is further gated by whether the PSID
   * endpoint actually resolves refs for each survey — a matched survey with
   * no PSIDs can never end up in the audience.
   */
  public readonly findSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AdvancedFilterActions.findSurveys),
      debounceTime(LOOKUP_DEBOUNCE_MS),
      switchMap(({ params }) =>
        this._surveyService.getSurveySummaries(params).pipe(
          switchMap((surveys) =>
            this._validateSurveyPSIDs(params, surveys).pipe(
              tap(({ surveys: validatedSurveys }) => {
                if (
                  params.filterType === ADVANCED_FILTER_TYPE.INCLUDE &&
                  surveys.length &&
                  !validatedSurveys.length
                ) {
                  this._toastService.warning(NO_PSIDS_FOUND_MESSAGE);
                }
              }),
            ),
          ),
          map(({ surveys, psidRefsBySurveyId }) =>
            AdvancedFilterApiActions.findSurveysSuccess({
              surveys,
              psidRefsBySurveyId,
            }),
          ),
          this.psCatchError((error) =>
            of(
              AdvancedFilterApiActions.findSurveysFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * This is used to show the total number of respondents for the current search criteria.
   */
  public readonly respondentCounts$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AdvancedFilterActions.getRespondentCounts),
      debounceTime(RESPONDENT_COUNT_DEBOUNCE_MS),
      switchMap(({ params: { filterType, allGroupIds, statuses } }) => {
        if (!allGroupIds.length) {
          return of(
            AdvancedFilterApiActions.respondentCountsSuccess({
              totalCount: 0,
            }),
          );
        }

        return this._psidService
          .getCounts(filterType === 'exclude', allGroupIds, statuses)
          .pipe(
            map((totalCount) =>
              AdvancedFilterApiActions.respondentCountsSuccess({
                totalCount,
              }),
            ),
            this.psCatchError((error) =>
              of(
                AdvancedFilterApiActions.respondentCountsFailure({
                  error: error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  /**
   * Search surveys for the Billing Number / Survey Title criteria selection.
   * It's a lightweight, single-keyword survey lookup, distinct from `findSurveys$`'s lookup.
   */
  public readonly criteriaSurveysSearch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AdvancedFilterActions.searchCriteriaSurveys),
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(({ params }) =>
        this._surveyService.searchSurveySummaries(params).pipe(
          map((surveys) =>
            AdvancedFilterApiActions.searchCriteriaSurveysSuccess({
              surveys,
            }),
          ),
          this.psCatchError((error) =>
            of(
              AdvancedFilterApiActions.searchCriteriaSurveysFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Search surveys for the manual groups selection. Similar to
   * `criteriaSurveysSearch$`, but with a different search type, and — in
   * Include mode — gated through the same PSID validation as `findSurveys$`.
   * No toast here: an Include-mode survey with no PSIDs simply never appears
   * as an option, the same "no results" feedback any empty search gives.
   */
  public readonly groupSurveysSearch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(AdvancedFilterActions.searchGroupSurveys),
      debounceTime(SEARCH_DEBOUNCE_MS),
      switchMap(({ params }) =>
        this._surveyService
          // The Groups field searches surveys by id; the wire key is the mapper's business.
          .searchSurveySummaries({ ...params, searchType: 'group' })
          .pipe(
            switchMap((surveys) => this._validateSurveyPSIDs(params, surveys)),
            map(({ surveys, psidRefsBySurveyId }) =>
              AdvancedFilterApiActions.searchGroupSurveysSuccess({
                surveys,
                psidRefsBySurveyId,
              }),
            ),
            this.psCatchError((error) =>
              of(
                AdvancedFilterApiActions.searchGroupSurveysFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  /**
   * React to `applyAdvancedFilters` — `clearUploadedPsidsFor` is set only
   * when the caller detected an Include→Exclude switch.
   */
  public readonly clearUploadedPsids$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.applyAdvancedFilters),
        map(({ clearUploadedPsidsFor }) => clearUploadedPsidsFor),
        filter((surveyId): surveyId is number => !!surveyId),
        switchMap((surveyId) =>
          this._psidService
            .clearUploadedPsids(surveyId)
            .pipe(this.psCatchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  );

  /**
   * Include mode only — resolves the actual PSID-linked surveys for a raw
   * lookup/search result, so a matched survey with no PSIDs never reaches the
   * audience. Shared by `findSurveys$` (criteria/date lookup) and
   * `groupSurveysSearch$` (manual Groups typeahead).
   */
  private _validateSurveyPSIDs(
    params: AdvancedFiltersLookupScope,
    surveys: SurveySummary[],
  ): Observable<{
    surveys: SurveySummary[];
    psidRefsBySurveyId: Record<number, string>;
  }> {
    if (params.filterType !== ADVANCED_FILTER_TYPE.INCLUDE || !surveys.length) {
      return of({ surveys, psidRefsBySurveyId: {} });
    }

    return this._psidService
      .getAll(
        params.dateRange,
        surveys.map((survey) => survey.id),
      )
      .pipe(
        map(({ surveyIds, refs }) => ({
          surveys: surveys.filter((survey) => surveyIds.includes(survey.id)),
          // `survey_ids`/`ref_list` are parallel arrays by backend contract —
          // no per-entry id, so index pairing is the only way to associate them.
          psidRefsBySurveyId: Object.fromEntries(
            surveyIds.map((surveyId, index) => [surveyId, refs[index]]),
          ),
        })),
      );
  }
}
